/*
 * Copyright (C) 2026 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

// Sanitize jquery's HTML-accepting DOM mutation methods at the prototype
// so every call site inherits sanitization. Covers $.fn.html (CFA-953)
// and the sibling methods .append/.prepend/.before/.after/.replaceWith
// (CFA-954). Loaded as a side-effect of jquery.instructure_jquery_patches
// (imported before @canvas/trusted-types in ui/index.ts), so the default
// policy never sees a string argument.
//
// Post-sanitize in place: let jQuery set innerHTML in its normal DOM
// context (preserves <tr>/<td> table structure), then sanitizeNode each
// inserted child where it sits, sharing hooks and allowlist with
// sanitizeHTML. Never relocate a node first -- reparenting breaks an
// <iframe>'s browsing context, a <select>'s selectedness, and SVG/MathML
// namespacing.
//
// Only string/function args to the six patched methods route through
// here; DOM nodes, jQuery objects, and other insertion paths pass through.
//
// sanitizeHTML's contract renames Rails UJS data-* attributes (data-url,
// data-method, etc.) to data-custom-* and filters inline style to the
// shared property allowlist -- both now apply to jQuery-inserted content.
//
// TrustedHTML arguments (already sanitized by @canvas/sanitize-html) are
// converted to plain strings before being passed to jQuery so that jQuery's
// internal typeof checks and string methods (e.g. htmlPrefilter, buildFragment)
// work correctly — TrustedHTML objects are not plain strings and cause
// "Cannot read properties of undefined" TypeErrors in jQuery's internals.
// After conversion the content goes through in-place sanitization (harmless
// double-pass on already-clean HTML). Non-string/non-function arguments
// (DOM nodes, jQuery objects, DocumentFragments) pass through unchanged since
// they do not involve HTML string parsing.
//
// Phase 2 migration path (require-trusted-types-for 'script' enforcement):
//   Step 1 (raw-string → jQuery innerHTML) will throw under enforcement.
//   Fix: replace the string arg with a pre-sanitized DocumentFragment before
//   calling originalHtml. Use DOMParser to preserve table context:
//     const doc = new DOMParser().parseFromString(sanitizeHTML(str), 'text/html')
//   `sanitizeHTML` returns TrustedHTML (RETURN_TRUSTED_TYPE:true), so the
//   DOMParser call receives a TrustedHTML object and bypasses the default policy.
//   Move child nodes into a DocumentFragment and pass the fragment to jQuery.
//   jQuery's fragment path uses appendChild() — no innerHTML — so TT is silent.
//   Table-structure context is preserved because DOMParser produces a full
//   <html><body> tree; caller context awareness shifts to the DOMParser path
//   (identical to how browsers handle full-page HTML). Adjust the function-arg
//   case similarly (call the function first, then sanitize its return value).

import $ from 'jquery'
import {sanitizeNode} from '@instructure/platform-sanitize'

// Opts into sanitizeHTML's SANITIZE_DOM:false variant — Canvas templates
// legitimately use name="name" (e.g. EditView's assignment name field), and
// DOM-clobbering protection already runs upstream on user content.
const SANITIZE_OPTIONS = {allowFormAttributeNames: true} as const

const isTrustedHTML = (value: unknown): value is TrustedHTML => {
  const tt = (window as Window & {trustedTypes?: {isHTML?: (v: unknown) => boolean}}).trustedTypes
  return Boolean(tt?.isHTML?.(value))
}

// Convert any TrustedHTML objects in args to plain strings.
// jQuery's internals (htmlPrefilter, buildFragment) call string methods like
// .replace() and .trim() on arguments — TrustedHTML objects lack these and
// cause TypeError crashes. String(trustedHtml) returns the underlying HTML.
const normalizeTrustedHTML = (args: unknown[]): unknown[] =>
  args.map(a => (isTrustedHTML(a) ? String(a) : a))

// <form> is [LegacyOverrideBuiltIns]: a named control shadows the same-named DOM
// property as an own property, so form.nodeType can be an <input>. Read through the
// prototype accessors, which no markup can shadow, or attacker HTML picks which
// branch of the walk it lands in.
const descriptorGet = (proto: object, prop: string): ((this: unknown) => unknown) =>
  Object.getOwnPropertyDescriptor(proto, prop)!.get!

const nodeTypeOf = descriptorGet(Node.prototype, 'nodeType') as (this: Node) => number
const childNodesOf = descriptorGet(Node.prototype, 'childNodes') as (this: Node) => NodeListOf<Node>
const parentNodeOf = descriptorGet(Node.prototype, 'parentNode') as (this: Node) => Node | null
const localNameOf = descriptorGet(Element.prototype, 'localName') as (this: Element) => string
const {removeChild} = Node.prototype

const {getAttributeNames, removeAttribute} = Element.prototype

const isElement = (node: Node): boolean => nodeTypeOf.call(node) === Node.ELEMENT_NODE

// Container attributes are the caller's, not the inserted HTML -- only strip handlers.
const stripEventHandlers = (el: Element): void => {
  for (const name of getAttributeNames.call(el)) {
    if (/^on/i.test(name)) removeAttribute.call(el, name)
  }
}

const drop = (node: Node): void => {
  const parent = parentNodeOf.call(node)
  if (parent) removeChild.call(parent, node)
}

const sanitizeEl = (el: Element): void => {
  try {
    sanitizeNode(el, SANITIZE_OPTIONS)
  } catch {
    // sanitizer can refuse a root outright; use drop(), not el.remove() (shadowable).
    drop(el)
  }
}

// instanceof is realm-bound: a template minted in an iframe document would fail it,
// and its children live in .content, so the walk would sanitize nothing.
const contentRootOf = (el: Element): Element | DocumentFragment =>
  localNameOf.call(el) === 'template' && 'content' in el ? (el as HTMLTemplateElement).content : el

// Return true when at least one arg requires in-place post-sanitization
// (i.e. is a plain string or function — not a DOM node/jQuery object).
const needsPostSanitize = (args: unknown[]): boolean =>
  args.some(a => typeof a === 'string' || typeof a === 'function')

// $.fn.html — getter on no-args, setter otherwise. Special-cased so the
// no-args getter doesn't go through sanitization.
const originalHtml = $.fn.html as unknown as (this: JQuery, ...args: unknown[]) => unknown

const sanitizingHtml = function (this: JQuery, ...args: unknown[]): unknown {
  if (args.length === 0) return originalHtml.call(this)

  // TrustedHTML objects → plain strings so jQuery's string methods work.
  const normalizedArgs = normalizeTrustedHTML(args)

  // String or function: let jQuery set content, then sanitize what it inserted.
  if (needsPostSanitize(normalizedArgs)) {
    originalHtml.apply(this, normalizedArgs)
    this.each(function (this: Element) {
      if (!isElement(this)) return
      stripEventHandlers(this)
      // a template parses into .content, so its children are never in childNodes
      const target = contentRootOf(this)
      // snapshot: sanitizing can detach a node, reindexing the live list
      Array.from(childNodesOf.call(target)).forEach(node => {
        if (isElement(node)) sanitizeEl(node as Element)
        // the sanitizer only keeps a nested comment beside an element sibling; top level has none
        else if (nodeTypeOf.call(node) === Node.COMMENT_NODE) drop(node)
      })
    })
    return this
  }

  // DOM nodes, jQuery objects, DocumentFragments, etc.: pass through.
  return originalHtml.apply(this, normalizedArgs)
}

$.fn.html = sanitizingHtml as unknown as typeof $.fn.html

// Sibling HTML-accepting setters.
const SIBLING_METHODS = ['append', 'prepend', 'before', 'after', 'replaceWith'] as const

type SiblingMethod = (typeof SIBLING_METHODS)[number]

// Methods that insert content as CHILDREN of `this` — sanitize `this`.
const CHILD_INSERTION_METHODS: ReadonlySet<SiblingMethod> = new Set(['append', 'prepend'] as const)

// Methods that insert content as SIBLINGS of `this` — sanitize `this`'s
// parent so the newly inserted siblings are covered.
const SIBLING_INSERTION_METHODS: ReadonlySet<SiblingMethod> = new Set([
  'before',
  'after',
  'replaceWith',
] as const)

for (const method of SIBLING_METHODS) {
  const original = $.fn[method] as unknown as (this: JQuery, ...args: unknown[]) => unknown

  const wrapped = function (this: JQuery, ...args: unknown[]) {
    // TrustedHTML objects → plain strings so jQuery's string methods work.
    const normalizedArgs = normalizeTrustedHTML(args)

    // No string/function args: delegate unchanged (DOM nodes, etc.).
    if (!needsPostSanitize(normalizedArgs)) {
      return original.apply(this, normalizedArgs)
    }

    if (CHILD_INSERTION_METHODS.has(method as 'append' | 'prepend')) {
      // Snapshot existing children so we sanitize only the NEW nodes —
      // not pre-existing siblings like <style> elements added via DOM API
      // (e.g. calendar per-course color rules from CFA-955).
      const snapshots: {parent: Element; before: Set<Node>}[] = []
      this.each(function (this: Element) {
        if (isElement(this))
          snapshots.push({parent: this, before: new Set(childNodesOf.call(this))})
      })
      original.apply(this, normalizedArgs)
      snapshots.forEach(({parent, before}) => {
        // Snapshot childNodes before walking: sanitizing can detach a node,
        // reindexing the live list and skipping its next sibling unsanitized.
        Array.from(childNodesOf.call(parent)).forEach(node => {
          if (!before.has(node) && isElement(node)) sanitizeEl(node as Element)
        })
      })
      return this
    }

    if (SIBLING_INSERTION_METHODS.has(method as 'before' | 'after' | 'replaceWith')) {
      // Collect parents and snapshot their children BEFORE insertion/removal.
      // .replaceWith detaches `this` first, so parents must be gathered before
      // the call. We snapshot children so we only sanitize the newly inserted
      // nodes — not pre-existing siblings (e.g. <style> elements from DOM API).
      const snapshots: {parent: Element; before: Set<Node>}[] = []
      this.each(function (this: Element) {
        const parent = parentNodeOf.call(this)
        if (parent && isElement(parent))
          snapshots.push({
            parent: parent as Element,
            before: new Set(childNodesOf.call(parent)),
          })
      })
      original.apply(this, normalizedArgs)
      snapshots.forEach(({parent, before}) => {
        Array.from(childNodesOf.call(parent)).forEach(node => {
          if (!before.has(node) && isElement(node)) sanitizeEl(node as Element)
        })
      })
      return this
    }

    return original.apply(this, normalizedArgs)
  }

  ;($.fn as unknown as Record<SiblingMethod, unknown>)[method] = wrapped
}

export {}
